import { useMemo, type CSSProperties } from "react";
import { Drawer } from "@mijadesign/web";
import type { DrawerProps } from "antd";
import { useDrawerStore } from "@mijalowcode/store";
import { DrawerRecordContext } from "../../contexts/DrawerContext";

type DrawerWrapperProps = DrawerProps & {
  drawerId?: string;
};

const DEFAULT_DRAWER_SIZE = 378;
const DEFAULT_BODY_STYLE = {
  minHeight: 200,
} satisfies CSSProperties;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// 将模板中的 {field} 替换为实际值
function resolveTemplate(
  template: string,
  record: Record<string, unknown>,
): string {
  let result = template;
  Object.keys(record).forEach((key) => {
    result = result.replace(
      new RegExp(`\\{${escapeRegExp(key)}\\}`, "g"),
      String(record[key] ?? ""),
    );
  });
  return result;
}

/**
 * Drawer 增强组件
 * - 受控模式：由 props.open 控制，不受 Store 影响
 * - 非受控模式：由 useDrawerStore 控制，ProTable action 触发
 * - 通过 DrawerRecordContext 向子组件提供 record 数据
 */
function DrawerWrapper(props: DrawerWrapperProps) {
  const {
    open: propsOpen,
    onClose: propsOnClose,
    title: propsTitle,
    children,
    drawerId: _drawerId,
    ...restProps
  } = props;

  const storeOpen = useDrawerStore((s) => s.open);
  const storeRecord = useDrawerStore((s) => s.record);
  const storeConfig = useDrawerStore((s) => s.config);
  const closeDrawer = useDrawerStore((s) => s.closeDrawer);

  // open 只表示显隐控制来源，受控与非受控逻辑在这里统一归并。
  const isPropsControlled = propsOpen !== undefined;
  const finalOpen = isPropsControlled ? propsOpen : storeOpen;

  // 解析 Store 中的 title 模板
  const storeTitle = useMemo(() => {
    if (!storeConfig.title || !storeRecord) return storeConfig.title;
    return resolveTemplate(storeConfig.title, storeRecord);
  }, [storeConfig.title, storeRecord]);

  // props 优先，Store 兜底
  const finalTitle = (propsTitle ?? storeTitle) || "抽屉";
  const finalPlacement =
    restProps.placement ?? storeConfig.placement ?? "right";
  const isHorizontalPlacement =
    finalPlacement === "left" || finalPlacement === "right";
  const finalWidth = restProps.width ?? storeConfig.width ?? DEFAULT_DRAWER_SIZE;
  const finalHeight =
    restProps.height ?? storeConfig.height ?? DEFAULT_DRAWER_SIZE;
  const drawerWidth = isHorizontalPlacement ? finalWidth : undefined;
  const drawerHeight = isHorizontalPlacement ? undefined : finalHeight;
  const finalClosable = restProps.closable ?? storeConfig.closable;
  const finalMask = restProps.mask ?? storeConfig.mask ?? true;
  const finalMaskClosable = restProps.maskClosable ?? storeConfig.maskClosable;
  const finalGetContainer = restProps.getContainer;
  const finalRootStyle =
    restProps.rootStyle ??
    (finalGetContainer === false
      ? { position: "absolute", inset: 0 }
      : undefined);
  const drawerShellClassName = [restProps.className, "lowcode-drawer-shell"]
    .filter(Boolean)
    .join(" ");
  const finalStyles = {
    ...restProps.styles,
    body: {
      ...DEFAULT_BODY_STYLE,
      ...restProps.styles?.body,
    },
  };

  const handleClose: DrawerProps["onClose"] = (e) => {
    if (propsOnClose) {
      propsOnClose(e);
    }
    if (!isPropsControlled) {
      closeDrawer();
    }
  };

  // Context 值：Store 中的 record
  const contextValue = useMemo(() => ({ record: storeRecord }), [storeRecord]);

  return (
    <DrawerRecordContext.Provider value={contextValue}>
      <Drawer
        {...restProps}
        title={finalTitle}
        open={finalOpen}
        placement={finalPlacement}
        width={drawerWidth}
        height={drawerHeight}
        closable={finalClosable}
        mask={finalMask}
        maskClosable={finalMaskClosable}
        getContainer={finalGetContainer}
        rootStyle={finalRootStyle}
        className={drawerShellClassName}
        styles={finalStyles}
        onClose={handleClose}
      >
        {children}
      </Drawer>
    </DrawerRecordContext.Provider>
  );
}

export default DrawerWrapper;
